import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import Webcam from 'react-webcam';
import { useDropzone } from 'react-dropzone';
import { CameraIcon, ArrowUpTrayIcon, QrCodeIcon, PhotoIcon, ChevronDownIcon, ChevronUpIcon, PencilIcon, TrashIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';

// Component to fetch full item details including all images
const FetchItemDetails = ({ itemId, render }) => {
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchDetails = async () => {
      if (!itemId) return;
      
      setIsLoading(true);
      try {
        const response = await api.get(`/api/inventory/${itemId}`);
        setDetails(response.data);
      } catch (err) {
        console.error('Error fetching item details:', err);
        setError('Failed to load item details');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDetails();
  }, [itemId]);
  
  if (isLoading) {
    return (
      <div className="py-2 text-center">
        <div className="animate-pulse inline-flex space-x-2 items-center">
          <div className="w-4 h-4 bg-primary-200 rounded-full"></div>
          <span className="text-xs text-primary-600">Loading details...</span>
        </div>
      </div>
    );
  }
  
  if (error) {
    return <div className="text-xs text-red-500 py-2">{error}</div>;
  }
  
  return render(details);
};

const ScannerPage = () => {
  const [activeTab, setActiveTab] = useState('barcode');
  const [capturing, setCapturing] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [scanQueue, setScanQueue] = useState([]);
  const [queueSize, setQueueSize] = useState(0);
  const [processingQueue, setProcessingQueue] = useState(false);
  const [selectedListId, setSelectedListId] = useState('');
  const [inventoryItems, setInventoryItems] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [nextInventoryNumber, setNextInventoryNumber] = useState('');
  const [selectedConsignorId, setSelectedConsignorId] = useState('');
  const webcamRef = useRef(null);
  const queryClient = useQueryClient();
  
  // Set up video constraints to use rear camera on mobile devices
  const videoConstraints = {
    facingMode: { ideal: "environment" }, // Prefer rear camera but don't require it
    width: { ideal: 1280 },
    height: { ideal: 720 }
  };
  
  // Handle camera errors
  const handleWebcamError = (error) => {
    console.error('Webcam error:', error);
    setError('Failed to access camera. Please check your permissions or try another method.');
  };
  
  // Keep track of total processed items in this session
  const [processedItems, setProcessedItems] = useState(0);
  const [totalScanned, setTotalScanned] = useState(0);

  // Fetch active lists for selection
  const { data: lists, isLoading: isLoadingLists } = useQuery('inventoryLists', async () => {
    try {
      const response = await api.get('/api/lists/active');
      return response.data;
    } catch (err) {
      console.error('Error fetching lists:', err);
      return [];
    }
  });

  // Fetch active consignors for selection
  const { data: consignors, isLoading: isLoadingConsignors } = useQuery('activeConsignors', async () => {
    try {
      const response = await api.get('/api/consignors/active');
      return response.data;
    } catch (err) {
      console.error('Error fetching consignors:', err);
      return [];
    }
  });

  // Use effect to set the default list when lists are loaded
  useEffect(() => {
    if (lists && lists.length > 0) {
      // Find the default list
      const defaultList = lists.find(list => list.defaultList);
      if (defaultList) {
        setSelectedListId(defaultList.id);
      } else if (lists[0]) {
        // If no default list, use the first one
        setSelectedListId(lists[0].id);
      }
    }
  }, [lists]);

  // Fetch inventory items for the selected list
  useEffect(() => {
    if (selectedListId) {
      fetchInventoryItems(selectedListId);
    }
  }, [selectedListId]);
  
  // Debug effect to monitor scanQueue changes and update queueSize
  useEffect(() => {
    console.log(`Queue state updated: Current size: ${scanQueue.length}`, scanQueue);
    setQueueSize(scanQueue.length);
  }, [scanQueue]);

  // Process scan queue effect
  useEffect(() => {
    const processQueue = async () => {
      if (isProcessing || processingQueue || scanQueue.length === 0) {
        return;
      }
      
      try {
        setProcessingQueue(true);
        setIsProcessing(true);
        
        // Take the first item from the queue
        const item = scanQueue[0];
        
        console.log(`Processing item from queue. Current queue size: ${scanQueue.length}`);
        
        // Process the item
        await saveScannedItem(item);
        
        // Remove the processed item from the queue
        setScanQueue(prev => {
          const newQueue = prev.slice(1);
          console.log(`Item processed. New queue size: ${newQueue.length}`);
          // Decrement queue size by 1 instead of setting it directly to newQueue.length
          setQueueSize(prevSize => Math.max(0, prevSize - 1));
          return newQueue;
        });
        
        // Update processed count
        setProcessedItems(prev => prev + 1);
        
      } catch (err) {
        console.error('Error processing queued item:', err);
        setError('Failed to process a queued item');
      } finally {
        setIsProcessing(false);
        setProcessingQueue(false);
      }
    };
    
    processQueue();
  }, [scanQueue, isProcessing, processingQueue]);

  // Function to fetch inventory items for a list with pagination
  const fetchInventoryItems = async (listId, page = 1) => {
    if (!listId) return;
    
    setIsLoadingItems(true);
    try {
      const response = await api.get(`/api/inventory?listId=${listId}&page=${page}&limit=${itemsPerPage}`);
      setInventoryItems(response.data.items);
      setTotalItems(response.data.total);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching inventory items:', err);
      setError('Failed to load inventory items');
    } finally {
      setIsLoadingItems(false);
    }
  };
  
  // Function to handle page changes
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchInventoryItems(selectedListId, newPage);
    }
  };

  // Handle manual barcode lookup - add to queue
  const handleBarcodeLookup = async () => {
    if (!barcode.trim()) {
      setError('Please enter a barcode');
      return;
    }

    setError(null);

    try {
      const response = await api.post('/api/scanner/barcode', { barcode });
      console.log('Barcode lookup response:', response.data);
      
      // If we have image data but no imageUrl, add it for consistency
      if (response.data.image && !response.data.imageUrl) {
        response.data.imageUrl = `${window.location.origin}${response.data.image.path}`;
      }
      
      // Add the scanned item to the queue
      setScanQueue(prev => {
        const newQueue = [...prev, response.data];
        console.log(`Adding item to queue from barcode. New queue size: ${newQueue.length}`);
        // Directly update queue size
        setQueueSize(newQueue.length);
        return newQueue;
      });
      
      // Clear the barcode input
      setBarcode('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to lookup barcode');
      console.error('Barcode lookup error:', err);
    }
  };

  // Handle image capture from webcam - instant capture with no UI blocking
  const handleCapture = useCallback(() => {
    setError(null);
    
    // Immediately get the screenshot without setting capturing state
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      setError('Failed to capture image');
      return;
    }
    
    // Optional: add a subtle visual flash effect to indicate capture without blocking UI
    const cameraElement = document.querySelector('.camera-container');
    if (cameraElement) {
      cameraElement.classList.add('flash');
      setTimeout(() => cameraElement.classList.remove('flash'), 150);
    }
    
    // Increment total count of scanned items
    setTotalScanned(prev => prev + 1);
    
    // Process the image in the background without blocking the UI
    (async () => {
      try {
        // Convert base64 image to blob for upload
        const blob = await (await fetch(imageSrc)).blob();
        
        console.log('Creating webcam capture blob:', {
          type: blob.type,
          size: blob.size
        });
        
        const formData = new FormData();
        formData.append('image', blob, 'webcam-capture.jpg');
        
        // Debug log the FormData contents
        console.log('FormData content:', 
          formData.get('image') ? 'Image data attached' : 'No image data'
        );
        
        const response = await api.post('/api/scanner/image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
      // Add the scanned item to the queue
      setScanQueue(prev => {
        const newQueue = [...prev, response.data];
        console.log(`Adding item to queue from camera. New queue size: ${newQueue.length}`);
        // Directly update queue size
        setQueueSize(newQueue.length);
        return newQueue;
      });
        
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to process image');
        console.error('Image processing error:', err);
      }
    })();
  }, [webcamRef]);

  // Handle file upload - add to queue (supports multiple files)
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    setError(null);
    
    // Show batch processing notification
    if (acceptedFiles.length > 1) {
      console.log(`Processing batch of ${acceptedFiles.length} images`);
    }
    
    try {
      // First, add all files to an array to maintain the exact selection order
      // Then reverse the array to process them in reverse order (as requested)
      const filesToProcess = [...acceptedFiles].reverse();
      
      // Immediately update the queue size to show the total number of files being processed
      // This ensures the UI shows the correct count right away
      const totalFilesToProcess = filesToProcess.length;
      console.log(`Setting initial queue size to ${totalFilesToProcess} for batch processing`);
      setQueueSize(prev => prev + totalFilesToProcess);
      
      // Process each file and add to queue in reverse order 
      // This ensures the first selected item gets the first inventory number
      for (let i = 0; i < filesToProcess.length; i++) {
        const file = filesToProcess[i];
        console.log(`Processing file ${i+1}/${filesToProcess.length}:`, {
          name: file.name,
          type: file.type,
          size: file.size
        });
        
        const formData = new FormData();
        formData.append('image', file);
        
        // Debug log the FormData contents
        console.log('FormData content (upload):', 
          formData.get('image') ? 'Image data attached' : 'No image data'
        );
        
        // Process images one by one in the order selected
        const response = await api.post('/api/scanner/image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Add the scanned item to the queue without updating the queue size again
        // since we've already updated it at the beginning
        setScanQueue(prev => {
          const newQueue = [...prev, response.data];
          console.log(`Adding item to queue from upload. Current queue size: ${newQueue.length}`);
          return newQueue;
        });
        
        // Increment total scanned count
        setTotalScanned(prev => prev + 1);
      }
      
      // Notify about batch completion
      if (acceptedFiles.length > 1) {
        console.log(`Added ${acceptedFiles.length} items to the scan queue in selected order`);
      }
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to process uploaded image(s)');
      console.error('Image upload error:', err);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    multiple: true // Explicitly enable multiple file selection
  });

  // Function to save a scanned item to inventory (auto-save)
  const saveScannedItem = async (item) => {
    if (!item) return;
    
    try {
      console.log('Auto-saving scanned item:', item);
      
      // Create an inventory item with the scanned data
      const inventoryData = {
        title: item.title || 'Unknown Product',
        description: item.description || '',
        upc: item.upc || '',
        category: item.category || '',
        tags: Array.isArray(item.tags) ? item.tags : [],
        binCode: item.binCode || '',
        locationCode: item.locationCode || '',
        listId: item.listId || selectedListId || null,
        consignorId: selectedConsignorId || null
      };
      
      // Apply inventory number if one has been set
      if (nextInventoryNumber && nextInventoryNumber.trim()) {
        console.log('Using inventory number from sequence:', nextInventoryNumber);
        inventoryData.inventoryNumber = nextInventoryNumber;
        
        // After using the inventory number, clear it so it's not used again
        // The backend will generate the next number in sequence automatically
        setNextInventoryNumber('');
      }
      
      // Log the full scanned item for debugging
      console.log('Full scanned item data:', JSON.stringify(item));
      
      // Check if we have multiple images from barcode lookup
      if (item.images && Array.isArray(item.images) && item.images.length > 0) {
        console.log(`Including ${item.images.length} images from barcode lookup`);
        
        // Add all images to the inventory data
        inventoryData.images = item.images.map(image => ({
          path: image.path,
          filename: image.filename || '',
          mimetype: image.mimetype || 'image/jpeg',
          size: image.size || 0
        }));
        
        // For backward compatibility, also set the primary image
        inventoryData.image = inventoryData.images[0];
        
        console.log(`Prepared ${inventoryData.images.length} images for saving to inventory`);
      }
      // If no multiple images, fall back to single image handling
      else if (item.image && item.image.path) {
        console.log('Including image information with inventory item:', item.image);
        
        // Include the image data in the request
        inventoryData.image = {
          path: item.image.path,
          filename: item.image.filename || '',
          mimetype: item.image.mimetype || 'image/jpeg',
          size: item.image.size || 0
        };
        
        // Add a flag to indicate if this came from barcode lookup
        if (item.fromBarcodeLookup) {
          console.log('This image came from barcode lookup:', item.image.path);
        }
      } else if (item.imageUrl) {
        // If we have imageUrl but not a structured image object, create one
        console.log('Creating image object from imageUrl:', item.imageUrl);
        const filename = item.imageUrl.split('/').pop();
        
        // Determine if the URL is absolute or relative
        const isAbsoluteUrl = item.imageUrl.startsWith('http') || item.imageUrl.startsWith('//');
        const path = isAbsoluteUrl ? item.imageUrl : item.imageUrl.replace(window.location.origin, '');
        
        console.log('Processed image path:', path);
        
        inventoryData.image = {
          path: path,
          filename: filename,
          mimetype: 'image/jpeg',
          size: 0
        };
        
        // Log if this came from barcode lookup
        if (item.fromBarcodeLookup) {
          console.log('This image URL came from barcode lookup:', path);
        }
      }
      
      console.log('Sending inventory data:', inventoryData);
      
      // Create the inventory item with image information
      const response = await api.post('/api/inventory', inventoryData);
      console.log('Item saved successfully:', response.data);
      
      // Refresh inventory items list
      await fetchInventoryItems(inventoryData.listId);
      
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save item');
      console.error('Save item error:', err);
      throw err;
    }
  };
  
  // Function to handle image file selection for an item being edited
  const handleItemImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Preview the image
      const reader = new FileReader();
      reader.onload = (event) => {
        setEditingItem({
          ...editingItem, 
          newImageFile: file,
          imagePreview: event.target.result
        });
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Function to update an existing inventory item
  const updateInventoryItem = async (itemId, updatedData) => {
    if (!itemId) return;
    
    try {
      console.log('Updating inventory item:', itemId, updatedData);
      
      // Check if we have a new image file to upload
      if (updatedData.newImageFile) {
        const formData = new FormData();
        formData.append('images', updatedData.newImageFile); // Note: 'images' is the field name the backend expects
        
        // Upload the image first
        console.log('Uploading new image for item');
        const imageResponse = await api.post(`/api/inventory/${itemId}/images`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        console.log('Image uploaded successfully:', imageResponse.data);
        
        // Remove the temporary image properties before updating the item
        delete updatedData.newImageFile;
        delete updatedData.imagePreview;
      }
      
      // Update the inventory item
      const response = await api.put(`/api/inventory/${itemId}`, updatedData);
      console.log('Item updated successfully:', response.data);
      
      // Refresh inventory items list
      await fetchInventoryItems(selectedListId);
      
      // Clear editing state
      setEditingItem(null);
      
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update item');
      console.error('Update item error:', err);
      throw err;
    }
  };
  
  // Function to toggle item expanded state
const toggleItemExpanded = (itemId) => {
    // If we're currently editing an item, don't close the accordion when toggling
    if (editingItem && editingItem.id === itemId) {
      return;
    }
    
    setExpandedItemId(expandedItemId === itemId ? null : itemId);
    setEditingItem(null);
  };
  
  // Function to start editing an item
  const startEditingItem = (item) => {
    // Make sure the item is expanded when editing
    setExpandedItemId(item.id);
    // Create a deep copy of the item to avoid direct state mutation
    setEditingItem({...item});
  };
  
  // Function to cancel editing
  const cancelEditing = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      setEditingItem(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Scanner</h1>
        <p className="mt-1 text-sm text-gray-500">
          Scan items using camera, upload images, or enter barcodes manually.
        </p>
      </div>

      {/* Scanner tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('barcode')}
            className={`${activeTab === 'barcode' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <QrCodeIcon className="h-5 w-5 mr-2" />
            Manual Barcode
          </button>

          <button
            onClick={() => setActiveTab('camera')}
            className={`${activeTab === 'camera' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <CameraIcon className="h-5 w-5 mr-2" />
            Camera
          </button>

          <button
            onClick={() => setActiveTab('upload')}
            className={`${activeTab === 'upload' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
            Upload
          </button>
        </nav>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Scanner input section */}
        <div>
          <div className="bg-white shadow rounded-lg p-6">
            {activeTab === 'camera' && (
              <div className="flex flex-col">
                <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-4 aspect-w-4 aspect-h-3 camera-container">
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    videoConstraints={videoConstraints}
                    onUserMediaError={handleWebcamError}
                    screenshotFormat="image/jpeg"
                    className="w-full h-full object-cover"
                  />
                  <style jsx>{`
                    .camera-container.flash {
                      position: relative;
                    }
                    .camera-container.flash::after {
                      content: '';
                      position: absolute;
                      top: 0;
                      left: 0;
                      right: 0;
                      bottom: 0;
    background-color: #065f46; /* dark green color */
    opacity: 0.5;
                      animation: flash 0.15s ease-out;
                      pointer-events: none;
                    }
                    @keyframes flash {
                      0% { opacity: 0.6; }
                      100% { opacity: 0; }
                    }
                  `}</style>
                </div>
                <button
                  onClick={handleCapture}
                  className="btn-primary flex justify-center items-center mt-4"
                >
                  <PhotoIcon className="h-5 w-5 mr-2" />
                  Capture & Add to Queue
                </button>
              </div>
            )}

            {activeTab === 'upload' && (
              <div className="flex flex-col">
                <div 
                  {...getRootProps()} 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-primary-500 flex flex-col items-center justify-center min-h-[240px]"
                >
                  <input {...getInputProps()} />
                  <ArrowUpTrayIcon className="h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">
                    {isDragActive
                      ? "Drop the images here"
                      : "Drag & drop images here, or click to select multiple files"}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Supported formats: JPG, PNG • Select multiple files to batch process
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'barcode' && (
              <div className="flex flex-col">
                <label htmlFor="barcode" className="block text-sm font-medium text-gray-700 mb-1">
                  Barcode / UPC
                </label>
                <div className="flex">
                  <input
                    type="text"
                    id="barcode"
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    placeholder="Enter barcode number"
                    className="form-input rounded-r-none flex-grow"
                  />
                  <button
                    onClick={handleBarcodeLookup}
                    className="btn-primary rounded-l-none"
                  >
                    Add to Queue
                  </button>
                </div>
              </div>
            )}


            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
                <span className="block sm:inline">{error}</span>
              </div>
            )}

            {isProcessing && (
              <div className="mt-4 flex justify-center">
                <div className="animate-pulse flex space-x-4 items-center">
                  <div className="w-6 h-6 bg-primary-200 rounded-full"></div>
                  <div className="text-primary-600">Processing...</div>
                </div>
              </div>
            )}
          </div>
        </div>

            {/* Status section */}
            <div>
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Scan Status</h2>
                
                {/* Inventory Number Setting */}
            {/* Inventory Number Setting */}
            <div className="mb-4 border-b pb-4">
              <label htmlFor="inventoryNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Set Starting Inventory Number
              </label>
              <div className="flex">
                <input
                  type="text"
                  id="inventoryNumber"
                  value={nextInventoryNumber}
                  onChange={(e) => setNextInventoryNumber(e.target.value)}
                  placeholder="e.g. A0001"
                  className="form-input rounded-r-none flex-grow text-sm"
                />
                <button
                  onClick={() => {
                    if (nextInventoryNumber.trim()) {
                      console.log('Setting next inventory number:', nextInventoryNumber);
                      // Show visual confirmation
                      const btnElement = document.querySelector('#setInventoryNumberBtn');
                      if (btnElement) {
                        btnElement.innerText = 'Set!';
                        setTimeout(() => {
                          btnElement.innerText = 'Set Sequence';
                        }, 1500);
                      }
                    }
                  }}
                  id="setInventoryNumberBtn"
                  className="btn-primary rounded-l-none text-sm px-3 py-2"
                >
                  Set Sequence
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Set the inventory number for the next scanned item. Subsequent scans will increment from this number.
              </p>
            </div>

            {/* Default Consignor Setting */}
            <div className="mb-4 border-b pb-4">
              <label htmlFor="defaultConsignor" className="block text-sm font-medium text-gray-700 mb-1">
                Default Consignor
              </label>
              <div className="flex">
                <select
                  id="defaultConsignor"
                  value={selectedConsignorId}
                  onChange={(e) => setSelectedConsignorId(e.target.value)}
                  className="form-input flex-grow text-sm"
                >
                  <option value="">No default consignor</option>
                  {isLoadingConsignors ? (
                    <option disabled>Loading consignors...</option>
                  ) : (
                    consignors?.map(consignor => (
                      <option key={consignor.id} value={consignor.id}>{consignor.name}</option>
                    ))
                  )}
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Set a default consignor to automatically assign to all scanned items.
              </p>
            </div>
                
                <div className="text-center py-4">
                  <div className="flex flex-col items-center">
                    <PhotoIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-500">
                      Ready to scan. Items will be added to the queue for processing.
                    </p>
                    <p className="text-sm text-gray-400 mt-2">
                      {isProcessing ? "Processing items..." : "You can continue scanning while items are being processed."}
                    </p>
                  </div>
                </div>
            
            {/* Queue Status */}
            <div className="mt-4 border-t pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Scan Queue</h3>
              
  <div className="bg-gray-50 rounded-lg p-5 text-center shadow-sm border border-gray-200">
                <div className="flex flex-col items-center justify-center">
                  {/* Status number - always visible */}
                  <div className="grid grid-cols-2 gap-6 w-full max-w-xs mb-2">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary-600">
                        {queueSize}
                      </div>
                      <div className="text-xs text-gray-600">Items Left</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{processedItems}</div>
                      <div className="text-xs text-gray-600">Processed</div>
                    </div>
                  </div>
                  
                  {/* Status message */}
                  {queueSize > 0 ? (
                    <div className="text-gray-700 mt-1">
                      {queueSize} item{queueSize !== 1 ? 's' : ''} waiting to be processed
                    </div>
                  ) : isProcessing ? (
                    <div className="animate-pulse flex space-x-2 items-center mt-1">
                      <div className="w-3 h-3 bg-primary-200 rounded-full"></div>
                      <div className="text-primary-600">Processing last item...</div>
                    </div>
                  ) : (
                    <div className="text-gray-500 mt-1">
                      {processedItems > 0 ? `All items processed. Ready for more scans.` : 'Queue empty. Ready for scanning.'}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
                <span className="block sm:inline">{error}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inventory List Section */}
      <div className="mt-8">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Current Inventory List</h2>
            
            <div className="flex items-center">
              {isLoadingLists ? (
                <div className="text-gray-500">Loading lists...</div>
              ) : (
                <select 
                  className="form-input max-w-xs"
                  value={selectedListId}
                  onChange={(e) => setSelectedListId(e.target.value)}
                >
                  <option value="">Select a list</option>
                  {lists?.map(list => (
                    <option key={list.id} value={list.id}>{list.name}</option>
                  ))}
                </select>
              )}
              
              <button 
                className="btn-secondary ml-2 text-xs py-1 px-2"
                onClick={() => fetchInventoryItems(selectedListId)}
              >
                Refresh
              </button>
            </div>
          </div>

          {isLoadingItems ? (
            <div className="text-center py-8">
              <div className="animate-pulse flex space-x-4 items-center justify-center">
                <div className="w-6 h-6 bg-primary-200 rounded-full"></div>
                <div className="text-primary-600">Loading inventory items...</div>
              </div>
            </div>
          ) : inventoryItems.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <p>No items in this list yet. Scan items to add them.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {inventoryItems.map((item) => (
                <div key={item.id} className="py-3">
                  {/* Item Header - always visible */}
                  <div 
                    className="flex items-center justify-between py-2 px-2 hover:bg-gray-50 rounded-md"
                  >
                    <div 
                      className="flex items-center space-x-3 cursor-pointer flex-grow"
                      onClick={() => toggleItemExpanded(item.id)}
                    >
                      {/* Inventory Number Badge - Displayed First */}
                      {item.inventoryNumber && (
                        <div className="flex-shrink-0 bg-blue-50 text-blue-700 font-semibold px-2 py-1 rounded-md text-sm min-w-[50px] text-center">
                          #{item.inventoryNumber}
                        </div>
                      )}
                    
                      <div className="flex-shrink-0">
                        {item.primaryImage ? (
                          <img 
                            src={item.primaryImage.path} 
                            alt={item.title}
                            className="h-12 w-12 object-cover rounded border border-gray-200" 
                          />
                        ) : (
                          <div className="h-12 w-12 bg-gray-100 rounded flex items-center justify-center">
                            <PhotoIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{item.title}</h3>
                        <p className="text-xs text-gray-500">
                          {item.upc ? `UPC: ${item.upc}` : ''} 
                          {item.category ? (item.upc ? ` • ` : '') + `Category: ${item.category}` : ''}
                        </p>
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => startEditingItem(item)}
                        className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                        title="Edit Item"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          
                          // Create a new inventory item by duplicating the current one
                          const duplicateItem = async () => {
                            try {
                              setError(null);
                              
                              // First get the full item details
                              const response = await api.get(`/api/inventory/${item.id}`);
                              const itemToDuplicate = response.data;
                              
                              // Prepare data for the new item
                              const duplicateData = {
                                title: `${itemToDuplicate.title} (Copy)`,
                                description: itemToDuplicate.description,
                                upc: itemToDuplicate.upc,
                                category: itemToDuplicate.category,
                                tags: itemToDuplicate.tags,
                                binCode: itemToDuplicate.binCode,
                                locationCode: itemToDuplicate.locationCode,
                                listId: itemToDuplicate.listId,
                                consignorId: itemToDuplicate.consignorId,
                                estimatedValue: itemToDuplicate.estimatedValue,
                                quantity: itemToDuplicate.quantity || 1
                              };
                              
                              // If the original item has images, we'll include them
                              if (itemToDuplicate.images && itemToDuplicate.images.length > 0) {
                                // Use the same image format expected by the backend
                                duplicateData.images = itemToDuplicate.images.map(image => ({
                                  path: image.path,
                                  filename: image.filename || '',
                                  mimetype: image.mimetype || 'image/jpeg',
                                  size: image.size || 0
                                }));
                                
                                // For backward compatibility, also set the primary image
                                const primaryImage = itemToDuplicate.images.find(img => img.isPrimary) || itemToDuplicate.images[0];
                                duplicateData.image = {
                                  path: primaryImage.path,
                                  filename: primaryImage.filename || '',
                                  mimetype: primaryImage.mimetype || 'image/jpeg',
                                  size: primaryImage.size || 0
                                };
                              }
                              
                              // Create the duplicate item
                              const createResponse = await api.post('/api/inventory', duplicateData);
                              console.log('Item duplicated successfully:', createResponse.data);
                              
                              // Show success message
                              const successMessage = document.createElement('div');
                              successMessage.className = 'fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded shadow-md z-50';
                              successMessage.innerHTML = '<span class="block sm:inline">Item duplicated successfully</span>';
                              document.body.appendChild(successMessage);
                              
                              // Remove the message after 3 seconds
                              setTimeout(() => {
                                if (document.body.contains(successMessage)) {
                                  document.body.removeChild(successMessage);
                                }
                              }, 3000);
                              
                              // Refresh inventory items list
                              await fetchInventoryItems(selectedListId, currentPage);
                              
                            } catch (err) {
                              console.error('Error duplicating item:', err);
                              setError(err.response?.data?.message || 'Failed to duplicate item');
                            }
                          };
                          
                          duplicateItem();
                        }}
                        className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                        title="Duplicate Item"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Are you sure you want to delete this item?')) {
                            // Call API to delete the item
                            api.delete(`/api/inventory/${item.id}`)
                              .then(() => {
                                // Refresh the inventory list
                                fetchInventoryItems(selectedListId, currentPage);
                              })
                              .catch(err => {
                                console.error('Error deleting item:', err);
                                setError(err.response?.data?.message || 'Failed to delete item');
                              });
                          }
                        }}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete Item"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleItemExpanded(item.id)}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                        title={expandedItemId === item.id ? "Collapse" : "Expand"}
                      >
                        {expandedItemId === item.id ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Expanded Content - only visible when expanded */}
                  {expandedItemId === item.id && !editingItem && (
                    <div className="mt-2 ml-16 mr-2 p-3 bg-gray-50 rounded-md">
                      {/* Get full item details with all images */}
                      <FetchItemDetails 
                        itemId={item.id} 
                        render={(itemDetails) => (
                          <>
                            {/* Image Gallery */}
                            {itemDetails?.images && itemDetails.images.length > 0 && (
                              <div className="mb-4">
                                <h4 className="text-xs font-medium text-gray-700 mb-2">Images ({itemDetails.images.length})</h4>
                                <div className="flex flex-wrap gap-3">
                                  {itemDetails.images.map((image) => (
                                    <div key={image.id} className="relative group">
                                      <img 
                                        src={image.path} 
                                        alt={item.title} 
                                        className="h-16 w-16 object-cover rounded border border-gray-200"
                                      />
                                      {image.isPrimary && (
                                        <span className="absolute bottom-0 right-0 bg-primary-500 text-white text-xs px-1 rounded-tl">
                                          Primary
                                        </span>
                                      )}
                                      
                                      {/* Image Actions - only visible on hover */}
                                      <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded">
                                        {!image.isPrimary && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              // Set as primary image
                                              api.put(`/api/inventory/${item.id}/images/${image.id}/primary`)
                                                .then(() => {
                                                  // Refresh item details
                                                  fetchInventoryItems(selectedListId, currentPage);
                                                })
                                                .catch(err => {
                                                  console.error('Error setting primary image:', err);
                                                  setError(err.response?.data?.message || 'Failed to set primary image');
                                                });
                                            }}
                                            className="p-1 text-white hover:text-primary-300 mr-1"
                                            title="Set as primary"
                                          >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                          </button>
                                        )}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.confirm('Are you sure you want to delete this image?')) {
                                              // Delete image
                                              api.delete(`/api/inventory/${item.id}/images/${image.id}`)
                                                .then(() => {
                                                  // Show success message
                                                  const successMessage = document.createElement('div');
                                                  successMessage.className = 'fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded shadow-md z-50';
                                                  successMessage.innerHTML = '<span class="block sm:inline">Image deleted successfully</span>';
                                                  document.body.appendChild(successMessage);
                                                  
                                                  // Remove the message after 3 seconds
                                                  setTimeout(() => {
                                                    if (document.body.contains(successMessage)) {
                                                      document.body.removeChild(successMessage);
                                                    }
                                                  }, 3000);
                                                  
                                                  // Refresh item details
                                                  fetchInventoryItems(selectedListId, currentPage);
                                                })
                                                .catch(err => {
                                                  console.error('Error deleting image:', err);
                                                  setError(err.response?.data?.message || 'Failed to delete image');
                                                });
                                            }
                                          }}
                                          className="p-1 text-white hover:text-red-300"
                                          title="Delete image"
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                              <div>
                                <dt className="text-gray-500">Inventory Number</dt>
                                <dd className="mt-1 text-gray-900">{item.inventoryNumber || 'Not assigned'}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Consignor</dt>
                                <dd className="mt-1 text-gray-900">{item.consignorName || 'Not assigned'}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Description</dt>
                                <dd className="mt-1 text-gray-900">{item.description || 'No description'}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">UPC/Barcode</dt>
                                <dd className="mt-1 text-gray-900">{item.upc || 'N/A'}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Category</dt>
                                <dd className="mt-1 text-gray-900">{item.category || 'Uncategorized'}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Bin Code</dt>
                                <dd className="mt-1 text-gray-900">{item.binCode || 'N/A'}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Location Code</dt>
                                <dd className="mt-1 text-gray-900">{item.locationCode || 'N/A'}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Added On</dt>
                                <dd className="mt-1 text-gray-900">{new Date(item.createdAt).toLocaleString()}</dd>
                              </div>
                            </dl>
                            
                            {/* Action buttons below details */}
                            <div className="mt-4 flex justify-end space-x-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditingItem(item);
                                }}
                                className="px-3 py-1.5 text-xs font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 rounded-md flex items-center"
                              >
                                <PencilIcon className="h-3.5 w-3.5 mr-1" />
                                Edit Item
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  
                                  // Create a new inventory item by duplicating the current one
                                  const duplicateItem = async () => {
                                    try {
                                      setError(null);
                                      
                                      // First get the full item details
                                      const response = await api.get(`/api/inventory/${item.id}`);
                                      const itemToDuplicate = response.data;
                                      
                                      // Prepare data for the new item
                                      const duplicateData = {
                                        title: `${itemToDuplicate.title} (Copy)`,
                                        description: itemToDuplicate.description,
                                        upc: itemToDuplicate.upc,
                                        category: itemToDuplicate.category,
                                        tags: itemToDuplicate.tags,
                                        binCode: itemToDuplicate.binCode,
                                        locationCode: itemToDuplicate.locationCode,
                                        listId: itemToDuplicate.listId,
                                        consignorId: itemToDuplicate.consignorId,
                                        estimatedValue: itemToDuplicate.estimatedValue,
                                        quantity: itemToDuplicate.quantity || 1
                                      };
                                      
                                      // If the original item has images, we'll include them
                                      if (itemToDuplicate.images && itemToDuplicate.images.length > 0) {
                                        // Use the same image format expected by the backend
                                        duplicateData.images = itemToDuplicate.images.map(image => ({
                                          path: image.path,
                                          filename: image.filename || '',
                                          mimetype: image.mimetype || 'image/jpeg',
                                          size: image.size || 0
                                        }));
                                        
                                        // For backward compatibility, also set the primary image
                                        const primaryImage = itemToDuplicate.images.find(img => img.isPrimary) || itemToDuplicate.images[0];
                                        duplicateData.image = {
                                          path: primaryImage.path,
                                          filename: primaryImage.filename || '',
                                          mimetype: primaryImage.mimetype || 'image/jpeg',
                                          size: primaryImage.size || 0
                                        };
                                      }
                                      
                                      // Create the duplicate item
                                      const createResponse = await api.post('/api/inventory', duplicateData);
                                      console.log('Item duplicated successfully:', createResponse.data);
                                      
                                      // Show success message
                                      const successMessage = document.createElement('div');
                                      successMessage.className = 'fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded shadow-md z-50';
                                      successMessage.innerHTML = '<span class="block sm:inline">Item duplicated successfully</span>';
                                      document.body.appendChild(successMessage);
                                      
                                      // Remove the message after 3 seconds
                                      setTimeout(() => {
                                        if (document.body.contains(successMessage)) {
                                          document.body.removeChild(successMessage);
                                        }
                                      }, 3000);
                                      
                                      // Refresh inventory items list
                                      await fetchInventoryItems(selectedListId, currentPage);
                                      
                                    } catch (err) {
                                      console.error('Error duplicating item:', err);
                                      setError(err.response?.data?.message || 'Failed to duplicate item');
                                    }
                                  };
                                  
                                  duplicateItem();
                                }}
                                className="px-3 py-1.5 text-xs font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 rounded-md flex items-center"
                              >
                                <DocumentDuplicateIcon className="h-3.5 w-3.5 mr-1" />
                                Duplicate
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm('Are you sure you want to delete this item?')) {
                                    // Call API to delete the item
                                    api.delete(`/api/inventory/${item.id}`)
                                      .then(() => {
                                        // Refresh the inventory list
                                        fetchInventoryItems(selectedListId, currentPage);
                                      })
                                      .catch(err => {
                                        console.error('Error deleting item:', err);
                                        setError(err.response?.data?.message || 'Failed to delete item');
                                      });
                                  }
                                }}
                                className="px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 rounded-md flex items-center"
                              >
                                <TrashIcon className="h-3.5 w-3.5 mr-1" />
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      />
                    </div>
                  )}
                  
                  {/* Editing Form - only visible when editing */}
                  {editingItem && editingItem.id === item.id && (
                    <div className="mt-2 ml-2 mr-2 p-4 bg-gray-50 rounded-md border border-gray-200 shadow-sm">
                      <div className="space-y-3">
                        {/* Image Section */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">Product Images</label>

                          {/* Current Images Gallery */}
                          <FetchItemDetails 
                            itemId={item.id} 
                            render={(itemDetails) => (
                              <>
                                {itemDetails?.images && itemDetails.images.length > 0 && (
                                  <div className="mb-4">
                                    <h4 className="text-xs text-gray-600 mb-2">Current Images</h4>
                                    <div className="flex flex-wrap gap-3 mb-3">
                                      {itemDetails.images.map((image) => (
                                        <div key={image.id} className="relative group h-20 w-20 flex-shrink-0">
                                          <img 
                                            src={image.path} 
                                            alt={item.title} 
                                            className={`h-full w-full object-cover rounded border ${image.isPrimary ? 'border-primary-500' : 'border-gray-200'}`}
                                          />
                                          {image.isPrimary && (
                                            <span className="absolute bottom-0 right-0 bg-primary-500 text-white text-xs px-1 rounded-tl">
                                              Primary
                                            </span>
                                          )}
                                          
                                          {/* Image Actions - only visible on hover */}
                                          <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded">
                                            {!image.isPrimary && (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  // Set as primary image
                                                  api.put(`/api/inventory/${item.id}/images/${image.id}/primary`)
                                                    .then(() => {
                                                      // Show success message and refresh item form state
                                                      const successMessage = document.createElement('div');
                                                      successMessage.className = 'fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded shadow-md z-50';
                                                      successMessage.innerHTML = '<span class="block sm:inline">Primary image set successfully</span>';
                                                      document.body.appendChild(successMessage);
                                                      setTimeout(() => {
                                                        if (document.body.contains(successMessage)) {
                                                          document.body.removeChild(successMessage);
                                                        }
                                                      }, 3000);
                                                      
                                                      // Re-fetch the item details to update the form state
                                                      api.get(`/api/inventory/${item.id}`)
                                                        .then(response => {
                                                          // Update the editingItem state
                                                          setEditingItem(response.data);
                                                        })
                                                        .catch(err => {
                                                          console.error('Error fetching updated item:', err);
                                                        });
                                                    })
                                                    .catch(err => {
                                                      console.error('Error setting primary image:', err);
                                                      setError(err.response?.data?.message || 'Failed to set primary image');
                                                    });
                                                }}
                                                className="p-1 text-white hover:text-primary-300 mr-1"
                                                title="Set as primary"
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                </svg>
                                              </button>
                                            )}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (window.confirm('Are you sure you want to delete this image?')) {
                                                  // Delete image
                                                  api.delete(`/api/inventory/${item.id}/images/${image.id}`)
                                                    .then(() => {
                                                      // Success message
                                                      const successMessage = document.createElement('div');
                                                      successMessage.className = 'fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded shadow-md z-50';
                                                      successMessage.innerHTML = '<span class="block sm:inline">Image deleted successfully</span>';
                                                      document.body.appendChild(successMessage);
                                                      
                                                      // Remove message after 3 seconds
                                                      setTimeout(() => {
                                                        if (document.body.contains(successMessage)) {
                                                          document.body.removeChild(successMessage);
                                                        }
                                                      }, 3000);
                                                      
                                                      // Re-fetch the item details to update the form state
                                                      api.get(`/api/inventory/${item.id}`)
                                                        .then(response => {
                                                          // Update the editingItem state
                                                          setEditingItem(response.data);
                                                        })
                                                        .catch(err => {
                                                          console.error('Error fetching updated item:', err);
                                                        });
                                                    })
                                                    .catch(err => {
                                                      console.error('Error deleting image:', err);
                                                      setError(err.response?.data?.message || 'Failed to delete image');
                                                    });
                                                }
                                              }}
                                              className="p-1 text-white hover:text-red-300"
                                              title="Delete image"
                                            >
                                              <TrashIcon className="h-4 w-4" />
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          />

                          {/* Upload New Images */}
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                              {/* Preview of new image to upload */}
                              {editingItem.imagePreview ? (
                                <div className="relative h-24 w-24">
                                  <img 
                                    src={editingItem.imagePreview} 
                                    alt="Preview"
                                    className="h-24 w-24 object-cover rounded border border-primary-300 shadow-sm" 
                                  />
                                  <span className="absolute top-0 right-0 bg-primary-500 text-white text-xs px-1 py-0.5 rounded-bl">
                                    New
                                  </span>
                                </div>
                              ) : (
                                <div className="h-24 w-24 bg-gray-100 rounded border border-dashed border-gray-300 flex items-center justify-center">
                                  <PhotoIcon className="h-10 w-10 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-grow">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Upload a new image</label>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleItemImageChange}
                                className="block w-full text-xs text-gray-500
                                  file:mr-3 file:py-1.5 file:px-3
                                  file:rounded file:border-0
                                  file:text-xs file:font-medium
                                  file:bg-primary-50 file:text-primary-700
                                  hover:file:bg-primary-100"
                              />
                              <p className="mt-1 text-xs text-gray-400">JPG, PNG or GIF up to 5MB</p>
                              
                              {editingItem.imagePreview && (
                                <button
                                  onClick={() => setEditingItem({...editingItem, newImageFile: null, imagePreview: null})}
                                  className="mt-2 px-2 py-1 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded inline-flex items-center"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  Clear selection
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700">Title</label>
                          <input
                            type="text"
                            value={editingItem.title || ''}
                            onChange={(e) => setEditingItem({...editingItem, title: e.target.value})}
                            className="form-input mt-1 block w-full text-sm"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700">Description</label>
                          <textarea
                            value={editingItem.description || ''}
                            onChange={(e) => setEditingItem({...editingItem, description: e.target.value})}
                            rows={2}
                            className="form-input mt-1 block w-full text-sm"
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Inventory Number</label>
                            <input
                              type="text"
                              value={editingItem.inventoryNumber || ''}
                              onChange={(e) => setEditingItem({...editingItem, inventoryNumber: e.target.value})}
                              className="form-input mt-1 block w-full text-sm"
                              placeholder="e.g. A0001"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Alphanumeric (e.g. A0001). Next items will use sequential numbering.
                            </p>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700">UPC/Barcode</label>
                            <input
                              type="text"
                              value={editingItem.upc || ''}
                              onChange={(e) => setEditingItem({...editingItem, upc: e.target.value})}
                              className="form-input mt-1 block w-full text-sm"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Category</label>
                            <input
                              type="text"
                              value={editingItem.category || ''}
                              onChange={(e) => setEditingItem({...editingItem, category: e.target.value})}
                              className="form-input mt-1 block w-full text-sm"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Consignor</label>
                            <select
                              value={editingItem.consignorId || ''}
                              onChange={(e) => setEditingItem({...editingItem, consignorId: e.target.value})}
                              className="form-input mt-1 block w-full text-sm"
                            >
                              <option value="">No consignor</option>
                              {isLoadingConsignors ? (
                                <option disabled>Loading consignors...</option>
                              ) : (
                                consignors?.map(consignor => (
                                  <option key={consignor.id} value={consignor.id}>{consignor.name}</option>
                                ))
                              )}
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Bin Code</label>
                            <input
                              type="text"
                              value={editingItem.binCode || ''}
                              onChange={(e) => setEditingItem({...editingItem, binCode: e.target.value})}
                              className="form-input mt-1 block w-full text-sm"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Location Code</label>
                            <input
                              type="text"
                              value={editingItem.locationCode || ''}
                              onChange={(e) => setEditingItem({...editingItem, locationCode: e.target.value})}
                              className="form-input mt-1 block w-full text-sm"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Quantity</label>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={editingItem.quantity || 1}
                              onChange={(e) => setEditingItem({...editingItem, quantity: e.target.value})}
                              className="form-input mt-1 block w-full text-sm"
                            />
                          </div>
                        </div>
                        
                        <div className="flex justify-end space-x-3 pt-3">
                          <button
                            type="button"
                            onClick={cancelEditing}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              // Form validation
                              if (!editingItem.title?.trim()) {
                                setError('Item title is required');
                                return;
                              }
                              
                              // Clear any previous error
                              setError(null);
                              
                              // Update the item
                              updateInventoryItem(editingItem.id, editingItem)
                                .then(() => {
                                  // Show success message
                                  const successMessage = document.createElement('div');
                                  successMessage.className = 'fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded shadow-md z-50';
                                  successMessage.innerHTML = '<span class="block sm:inline">Item updated successfully</span>';
                                  document.body.appendChild(successMessage);
                                  
                                  // Remove the message after 3 seconds
                                  setTimeout(() => {
                                    if (document.body.contains(successMessage)) {
                                      document.body.removeChild(successMessage);
                                    }
                                  }, 3000);
                                })
                                .catch(err => {
                                  console.error('Error updating item:', err);
                                  setError(err.response?.data?.message || 'Failed to update item');
                                });
                            }}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-md"
                          >
                            Save Changes
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination Controls */}
          {!isLoadingItems && inventoryItems.length > 0 && (
            <div className="flex items-center justify-between border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center text-sm text-gray-500">
                Showing <span className="font-medium mx-1">{(currentPage - 1) * itemsPerPage + 1}</span> 
                to <span className="font-medium mx-1">
                  {Math.min(currentPage * itemsPerPage, totalItems)}
                </span> 
                of <span className="font-medium ml-1">{totalItems}</span> items
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className={`px-2 py-1 text-xs rounded ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-2 py-1 text-xs rounded ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Previous
                </button>
                
                <span className="px-2 py-1 text-xs bg-primary-50 text-primary-700 rounded border border-primary-200 flex items-center">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-2 py-1 text-xs rounded ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`px-2 py-1 text-xs rounded ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScannerPage;
